/**
 * Where a file lives on the person's disk, where the browser tells (the File
 * System Access API: Chrome, Edge and other Chromium browsers on computers).
 * A handle kept with a saved project lets the app read the same file again on
 * a later visit, once the person allows it, instead of asking them to find it.
 * Browsers without the API give no handles, and the app asks as before.
 *
 * Handles are remembered per `File` as files arrive (picked or dropped), so
 * whatever keeps a file can look its handle up later without every caller
 * passing it along.
 */

/** The parts of the API this uses; TypeScript's DOM types don't carry them yet. */
interface PermissionedHandle extends FileSystemFileHandle {
  queryPermission?(descriptor: { readonly mode: 'read' }): Promise<PermissionState>;
  requestPermission?(descriptor: { readonly mode: 'read' }): Promise<PermissionState>;
}

interface PickerType {
  readonly description?: string;
  readonly accept: Record<string, string[]>;
}

type OpenFilePicker = (options: { readonly multiple?: boolean; readonly types?: PickerType[]; readonly excludeAcceptAllOption?: boolean }) => Promise<FileSystemFileHandle[]>;

type HandleItem = DataTransferItem & { getAsFileSystemHandle?(): Promise<FileSystemHandle | null> };

const handles = new WeakMap<File, FileSystemFileHandle>();

/** The handle a file came with, if the browser gave one. */
export const handleOf = (file: File): FileSystemFileHandle | undefined => handles.get(file);

export function rememberHandle(file: File, handle: FileSystemFileHandle): void {
  handles.set(file, handle);
}

const openFilePicker = (): OpenFilePicker | undefined => (globalThis as { showOpenFilePicker?: OpenFilePicker }).showOpenFilePicker;

/** Whether picking files here gives handles (the system dialog of the File System Access API). */
export const picksWithHandles = (): boolean => typeof openFilePicker() === 'function';

/**
 * An input's `accept` ("video/*,.mkv,.mov") as the dialog's file types. The
 * dialog wants each extension under a type, so all of them go under the first
 * one; the other types follow on their own.
 */
export function pickerTypes(accept: string): PickerType[] {
  const tokens = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  const types = tokens.filter((token) => token.includes('/'));
  const extensions = tokens.filter((token) => token.startsWith('.'));
  if (types.length === 0) return extensions.length > 0 ? [{ accept: { 'application/octet-stream': extensions } }] : [];
  return [{ accept: Object.fromEntries(types.map((type, index) => [type, index === 0 ? extensions : []])) }];
}

/**
 * Files picked in the system dialog, each remembered with its handle; an empty
 * list when the person closes the dialog. Null where there is no such dialog,
 * or it refused the request: the caller then opens its file input instead.
 * Call it straight from the click, like the input it stands in for.
 */
export async function pickWithHandles(accept: string, multiple = true): Promise<File[] | null> {
  const open = openFilePicker();
  if (!open) return null;
  let picked: FileSystemFileHandle[];
  try {
    picked = await open.call(globalThis, { multiple, types: pickerTypes(accept), excludeAcceptAllOption: false });
  } catch (error) {
    // Closing the dialog is an answer; anything else (an `accept` it can't read, a blocked dialog) leaves it to the input.
    return error instanceof DOMException && error.name === 'AbortError' ? [] : null;
  }
  return Promise.all(
    picked.map(async (handle) => {
      const file = await handle.getFile();
      rememberHandle(file, handle);
      return file;
    }),
  );
}

/**
 * The dropped files, with their handles remembered as they arrive. The handles
 * can only be asked for during the drop event, so this must run in it; the
 * files are returned at once, the handles follow a moment later.
 */
export function droppedFiles(dataTransfer: DataTransfer): File[] {
  const files = Array.from(dataTransfer.files);
  const items = Array.from(dataTransfer.items ?? []).filter((item) => item.kind === 'file') as HandleItem[];
  // Both lists hold the dropped files in the same order; the name check guards against a browser that doesn't.
  items.forEach((item, index) => {
    const file = files[index];
    if (!file || typeof item.getAsFileSystemHandle !== 'function') return;
    void item
      .getAsFileSystemHandle()
      .then((handle) => {
        if (handle?.kind === 'file' && handle.name === file.name) rememberHandle(file, handle as FileSystemFileHandle);
      })
      .catch(() => undefined);
  });
  return files;
}

/** A kept handle read again: the file, or why not (the person said no, or it is gone or was moved). */
export type Reopened = { readonly file: File } | { readonly file: null; readonly refused: boolean };

/**
 * The file behind a kept handle, read again. Reading needs the person's
 * permission once per visit (or for good, when the browser offers "on every
 * visit"); `ask` lets the browser ask for it, which it only does right after a
 * tap or click.
 */
export async function reopen(handle: FileSystemFileHandle, { ask }: { readonly ask: boolean }): Promise<Reopened> {
  const permissioned = handle as PermissionedHandle;
  try {
    let permission = (await permissioned.queryPermission?.({ mode: 'read' })) ?? 'granted';
    if (permission === 'prompt' && ask && permissioned.requestPermission) permission = await permissioned.requestPermission({ mode: 'read' });
    if (permission !== 'granted') return { file: null, refused: true };
    const file = await handle.getFile();
    rememberHandle(file, handle);
    return { file };
  } catch {
    return { file: null, refused: false };
  }
}
